// State of the /events filter bar (D43/D44): its defaults, its URL form (so a filtered view can be
// bookmarked), its localStorage form, and the API query it turns into. Pure functions; the page
// owns the React state. Everything is cut on UTC boundaries.
import type { EventSortKey, EventSource, EventType, EventsQuery } from '../api/model';
import { parseUtcInput, presetWindow, toApiUtc, utcInputValue } from './time';

export const ALL_SOURCES: readonly EventSource[] = ['manual', 'binance', 'calculated'];
export const ALL_TYPES: readonly EventType[] = ['borrow', 'repay', 'buy', 'sell', 'interest', 'transfer', 'liquidation', 'balance_snapshot', 'result'];
export const PAGE_SIZES = [50, 100, 200] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export type Period = 'today' | '7d' | '30d' | 'all' | 'custom';
export const PERIODS: readonly Period[] = ['today', '7d', '30d', 'all', 'custom'];
export const SORT_KEYS: readonly EventSortKey[] = ['occurred_at', 'type', 'source', 'location', 'asset', 'display_quantity', 'price', 'fee_quantity', 'deal', 'note'];

export interface EventsFilter {
  sources: EventSource[];
  types: EventType[];
  locationId: string;
  asset: string;
  /** '' = any, 'none' = "Ügylet nélkül", else a deal id. */
  deal: string;
  period: Period;
  /** Custom period, UTC "YYYY-MM-DD HH:MM" (from inclusive, to exclusive). */
  from: string;
  to: string;
  includeVoided: boolean;
  /** D47: only repays with a part no borrow is linked to ("Hozzárendelésre vár"). */
  repayUnassigned: boolean;
  sort: EventSortKey;
  order: 'asc' | 'desc';
  size: PageSize;
  /** 1-based. */
  page: number;
}

export const DEFAULT_FILTER: EventsFilter = {
  sources: [...ALL_SOURCES], types: [...ALL_TYPES], locationId: '', asset: '', deal: '', period: 'all', from: '', to: '',
  includeVoided: false, repayUnassigned: false, sort: 'occurred_at', order: 'desc', size: 50, page: 1,
};

/** True when no FILTER is set (sort, page size and page do not count: "Szűrők törlése" keeps them). */
export function isDefaultFilter(f: EventsFilter): boolean {
  return (
    f.sources.length === ALL_SOURCES.length && f.types.length === ALL_TYPES.length && f.locationId === '' && f.asset === '' && f.deal === '' &&
    f.period === 'all' && !f.includeVoided && !f.repayUnassigned
  );
}

export function clearFilters(f: EventsFilter): EventsFilter {
  return { ...DEFAULT_FILTER, sort: f.sort, order: f.order, size: f.size };
}

const pickList = <T extends string>(raw: string | null, all: readonly T[]): T[] =>
  raw === null ? [...all] : all.filter((x) => raw.split(',').includes(x));

/** The URL query of a filter: only what differs from the default, so the plain page has a plain URL. */
export function filterToParams(f: EventsFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.sources.length !== ALL_SOURCES.length) p.set('src', f.sources.join(','));
  if (f.types.length !== ALL_TYPES.length) p.set('type', f.types.join(','));
  if (f.locationId) p.set('loc', f.locationId);
  if (f.asset) p.set('asset', f.asset);
  if (f.deal) p.set('deal', f.deal);
  if (f.period !== 'all') p.set('period', f.period);
  if (f.period === 'custom') {
    if (f.from) p.set('from', f.from);
    if (f.to) p.set('to', f.to);
  }
  if (f.includeVoided) p.set('voided', '1');
  if (f.repayUnassigned) p.set('unassigned', '1');
  if (f.sort !== DEFAULT_FILTER.sort || f.order !== DEFAULT_FILTER.order) {
    p.set('sort', f.sort);
    p.set('order', f.order);
  }
  if (f.size !== DEFAULT_FILTER.size) p.set('size', String(f.size));
  if (f.page > 1) p.set('page', String(f.page));
  return p;
}

const KNOWN = ['src', 'type', 'loc', 'asset', 'deal', 'period', 'from', 'to', 'voided', 'unassigned', 'sort', 'order', 'size', 'page'];

/** Parse a URL query; null when it carries none of our parameters (then the stored state applies). Bad values fall back to the default. */
export function paramsToFilter(p: URLSearchParams): EventsFilter | null {
  if (!KNOWN.some((k) => p.has(k))) return null;
  const period = p.get('period');
  const sort = p.get('sort');
  const size = Number(p.get('size'));
  const page = Number(p.get('page'));
  const deal = p.get('deal') ?? '';
  return {
    sources: pickList(p.get('src'), ALL_SOURCES),
    types: pickList(p.get('type'), ALL_TYPES),
    locationId: p.get('loc') ?? '',
    asset: (p.get('asset') ?? '').trim().toUpperCase(),
    deal: deal === 'none' || /^\d+$/.test(deal) ? deal : '',
    period: PERIODS.includes(period as Period) ? (period as Period) : 'all',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
    includeVoided: p.get('voided') === '1',
    repayUnassigned: p.get('unassigned') === '1',
    sort: SORT_KEYS.includes(sort as EventSortKey) ? (sort as EventSortKey) : DEFAULT_FILTER.sort,
    order: p.get('order') === 'asc' ? 'asc' : 'desc',
    size: PAGE_SIZES.includes(size as PageSize) ? (size as PageSize) : DEFAULT_FILTER.size,
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

export const STORAGE_KEY = 'moneyflow.events.filter.v1';

export function loadStoredFilter(): EventsFilter | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? paramsToFilter(new URLSearchParams(raw)) : null;
  } catch {
    return null; // private window, blocked storage...: the page works without it
  }
}

export function storeFilter(f: EventsFilter): void {
  try {
    // The page number is a position, not a preference: a bookmark keeps it, the stored state does not.
    window.localStorage.setItem(STORAGE_KEY, filterToParams({ ...f, page: 1 }).toString());
  } catch {
    /* storage unavailable: nothing to do */
  }
}

/** The state the page starts from: the URL if it carries a filter, else the stored one, else the defaults. */
export function initialFilter(search: string): EventsFilter {
  return paramsToFilter(new URLSearchParams(search)) ?? loadStoredFilter() ?? DEFAULT_FILTER;
}

export interface BuiltQuery {
  query: EventsQuery;
  /** Hungarian message when the custom period is not usable (the query then carries no time bound). */
  periodError: string | null;
  /** A filter that can match nothing (no source / no type selected): the page skips the request. */
  empty: boolean;
}

/** The API query of a filter. `now` is only used for the relative presets, which are cut on the UTC day. */
export function filterToQuery(f: EventsFilter, now: Date = new Date()): BuiltQuery {
  const q: EventsQuery = { sort: f.sort, order: f.order, limit: f.size, offset: (Math.max(f.page, 1) - 1) * f.size };
  if (f.sources.length < ALL_SOURCES.length) q.source = [...f.sources];
  if (f.types.length < ALL_TYPES.length) q.type = [...f.types];
  if (f.locationId) q.location_id = f.locationId;
  if (f.asset) q.asset = f.asset;
  if (f.deal === 'none') q.no_deal = true;
  else if (f.deal) q.deal_id = Number(f.deal);
  if (f.includeVoided) q.include_voided = true;
  if (f.repayUnassigned) q.repay_unassigned = true;
  let periodError: string | null = null;
  if (f.period === 'today') q.from = toApiUtc(presetWindow('today', now).from);
  else if (f.period === '7d') q.from = toApiUtc(presetWindow('week', now).from);
  else if (f.period === '30d') q.from = toApiUtc(presetWindow('month', now).from);
  else if (f.period === 'custom') {
    const from = f.from ? parseUtcInput(f.from) : null;
    const to = f.to ? parseUtcInput(f.to) : null;
    if ((f.from && !from) || (f.to && !to)) periodError = 'Az időpont formátuma: ÉÉÉÉ-HH-NN ÓÓ:PP (UTC).';
    else if (from && to && from >= to) periodError = 'A „-tól” időpontnak a „-ig” előtt kell lennie.';
    else {
      if (from) q.from = toApiUtc(from);
      if (to) q.to = toApiUtc(to);
    }
  }
  return { query: q, periodError, empty: f.sources.length === 0 || f.types.length === 0 };
}

/** Pre-fill for the custom period when the owner switches to it: the last 24 hours, UTC. */
export function defaultCustomRange(now: Date): { from: string; to: string } {
  return { from: utcInputValue(new Date(now.getTime() - 86_400_000)), to: utcInputValue(now) };
}
